import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { SessionTimeoutDialogComponent } from '../components/session-timeout-dialog/session-timeout-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
    private readonly WARNING_BEFORE = 2 * 60 * 1000;    // Show warning 2 min before timeout
    private readonly LAST_ACTIVITY_KEY = 'last_activity';
    // The API enforces the same idle timeout; tell it about activity that
    // makes no API call, at most this often.
    private readonly ACTIVITY_REPORT_INTERVAL = 60 * 1000;

    private idleTimer: any = null;
    private warningTimer: any = null;
    private warningDialogOpen = false;
    private warningDialogRef: MatDialogRef<SessionTimeoutDialogComponent> | null = null;
    private running = false;
    private lastActivityReport = 0;

    constructor(
        private authService: AuthService,
        private router: Router,
        private dialog: MatDialog,
        private ngZone: NgZone
    ) {}

    start(): void {
        if (this.running) return;
        this.running = true;
        this.updateLastActivity();
        this.setupListeners();
        this.resetTimers();
    }

    stop(): void {
        this.running = false;
        this.removeListeners();
        this.clearTimers();
    }

    ngOnDestroy(): void {
        this.stop();
    }

    /** Idle timeout from the API (SESSION_IDLE_TIMEOUT_MINUTES); 10 min by default. */
    private get idleTimeout(): number {
        return this.authService.getIdleTimeoutMs();
    }

    /** Warn 2 min before sign-out, or halfway for timeouts shorter than 4 min. */
    private get warningBefore(): number {
        return Math.min(this.WARNING_BEFORE, this.idleTimeout / 2);
    }

    private activityEvents = ['mousemove', 'click', 'keydown', 'scroll', 'touchstart'];

    private onActivity = (): void => {
        if (!this.running) return;
        // While the warning is up only its "continue" button counts as
        // activity, so the user is still signed out if they do not confirm.
        if (this.warningDialogOpen) return;
        this.updateLastActivity();
        this.reportActivity(false);
        this.resetTimers();
    };

    /** Throttled heartbeat to the API (see ACTIVITY_REPORT_INTERVAL). */
    private reportActivity(force: boolean): void {
        const now = Date.now();
        if (!force && now - this.lastActivityReport < this.ACTIVITY_REPORT_INTERVAL) return;
        this.lastActivityReport = now;
        this.ngZone.run(() => this.authService.reportActivity());
    }

    private setupListeners(): void {
        // Run outside Angular zone to avoid triggering change detection on every mouse move
        this.ngZone.runOutsideAngular(() => {
            this.activityEvents.forEach(event => {
                document.addEventListener(event, this.onActivity, { passive: true });
            });
        });
    }

    private removeListeners(): void {
        this.activityEvents.forEach(event => {
            document.removeEventListener(event, this.onActivity);
        });
    }

    private updateLastActivity(): void {
        localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());
    }

    private getLastActivity(): number {
        const stored = localStorage.getItem(this.LAST_ACTIVITY_KEY);
        return stored ? parseInt(stored, 10) : Date.now();
    }

    private resetTimers(): void {
        this.clearTimers();

        const lastActivity = this.getLastActivity();
        const elapsed = Date.now() - lastActivity;
        const remaining = this.idleTimeout - elapsed;
        const warningAt = this.idleTimeout - this.warningBefore - elapsed;

        if (remaining <= 0) {
            // Already timed out (e.g., came back from sleep)
            this.doLogout();
            return;
        }

        // Run timers outside Angular zone
        this.ngZone.runOutsideAngular(() => {
            if (warningAt > 0) {
                this.warningTimer = setTimeout(() => {
                    this.ngZone.run(() => this.onWarningDue());
                }, warningAt);
            } else if (remaining > 0) {
                // Already past warning threshold but not yet timed out
                this.ngZone.run(() => this.showWarning());
            }

            this.idleTimer = setTimeout(() => {
                this.ngZone.run(() => this.onIdleDue());
            }, remaining);
        });
    }

    // last_activity is shared by every tab, but activity events only reach the
    // tab in use. Before warning or signing out, re-read it: if the user has
    // been active in another tab, this one is not idle. (Signing out ends the
    // server session the tabs share, so a background tab must not do it.)

    /** Activity recorded (by any tab) within the last windowMs. */
    private activeWithin(windowMs: number): boolean {
        // A missing key means another tab already signed out: not active.
        const stored = localStorage.getItem(this.LAST_ACTIVITY_KEY);
        return !!stored && Date.now() - parseInt(stored, 10) < windowMs;
    }

    private onWarningDue(): void {
        if (this.activeWithin(this.idleTimeout - this.warningBefore)) {
            this.resetTimers();
            return;
        }
        this.showWarning();
    }

    private onIdleDue(): void {
        if (this.activeWithin(this.idleTimeout)) {
            if (this.warningDialogRef) {
                this.warningDialogRef.close('continue');
            } else {
                this.resetTimers();
            }
            return;
        }
        this.doLogout();
    }

    private clearTimers(): void {
        if (this.warningTimer) {
            clearTimeout(this.warningTimer);
            this.warningTimer = null;
        }
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    private showWarning(): void {
        if (this.warningDialogOpen) return;
        this.warningDialogOpen = true;

        const remaining = this.idleTimeout - (Date.now() - this.getLastActivity());
        const remainingMinutes = Math.max(1, Math.ceil(remaining / 60000));

        const dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
            width: '420px',
            disableClose: true,
            panelClass: 'session-timeout-dialog-panel',
            data: { minutes: remainingMinutes }
        });
        this.warningDialogRef = dialogRef;

        dialogRef.afterClosed().subscribe((action: string) => {
            this.warningDialogOpen = false;
            this.warningDialogRef = null;
            if (!this.running) return; // closed by doLogout's closeAll()
            if (action === 'continue') {
                this.updateLastActivity();
                this.reportActivity(true);
                this.resetTimers();
            } else {
                this.doLogout();
            }
        });
    }

    private doLogout(): void {
        this.stop();
        localStorage.removeItem(this.LAST_ACTIVITY_KEY);
        // Close any open dialogs
        this.dialog.closeAll();
        this.authService.logout();
        this.router.navigate(['/auth/login'], {
            queryParams: { reason: 'timeout' }
        });
    }
}
